/**
 * Styles for the embeddable widget.
 *
 * Delivered as a string and injected into a **shadow root**, which is the whole point: this
 * code lands on client websites whose CSS we have never seen. Without shadow encapsulation
 * their `button { ... }` rule would deform our widget, and our styles could leak into their
 * page. Nothing here uses global selectors or `!important` because it doesn't need to.
 *
 * The accent colour is applied through the `--bc-accent` custom property so a client can
 * rebrand via `data-color` without us shipping a stylesheet per customer.
 */
export const WIDGET_CSS = `
:host {
  --bc-accent: #003bff;
  --bc-accent-ink: #ffffff;
  --bc-surface: #ffffff;
  --bc-text: #111827;
  --bc-muted: #6b7280;
  --bc-border: #e5e7eb;
  --bc-danger: #dc2626;
  --bc-radius: 16px;
  --bc-shadow: 0 10px 40px rgba(0, 0, 0, 0.16);

  /* A high z-index is unavoidable on third-party sites, but stay below the max so a
     client's own critical overlay (cookie banner, checkout modal) can still sit above us. */
  position: fixed;
  z-index: 2147483000;
  bottom: 20px;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  line-height: 1.45;
}
:host([data-position="bottom-left"]) { left: 20px; }
:host(:not([data-position="bottom-left"])) { right: 20px; }

*, *::before, *::after { box-sizing: border-box; }

button {
  font: inherit;
  cursor: pointer;
  border: 0;
  background: none;
  color: inherit;
}

/* Visible focus ring for keyboard users — never remove this without a replacement. */
button:focus-visible, [tabindex]:focus-visible {
  outline: 2px solid var(--bc-accent);
  outline-offset: 2px;
  border-radius: 8px;
}

/* ── Launcher ─────────────────────────────────────────────────────────────── */
.launcher {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 13px 20px;
  border-radius: 999px;
  background: var(--bc-accent);
  color: var(--bc-accent-ink);
  font-size: 15px;
  font-weight: 500;
  box-shadow: var(--bc-shadow);
  transition: transform 150ms ease, box-shadow 150ms ease;
}
.launcher:hover { transform: translateY(-1px); box-shadow: 0 14px 44px rgba(0,0,0,.2); }
.launcher[hidden] { display: none; }

/* ── Panel ────────────────────────────────────────────────────────────────── */
.panel {
  width: 340px;
  max-width: calc(100vw - 40px);
  background: var(--bc-surface);
  color: var(--bc-text);
  border-radius: var(--bc-radius);
  box-shadow: var(--bc-shadow);
  overflow: hidden;
  animation: bc-rise 180ms ease;
}
.panel[hidden] { display: none; }

@keyframes bc-rise {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
/* Honour a user's motion preference rather than animating regardless. */
@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
  .launcher { transition: none; }
  .pulse { animation: none; }
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bc-accent);
  color: var(--bc-accent-ink);
}
.head h2 { margin: 0; font-size: 15px; font-weight: 600; }
.head p  { margin: 2px 0 0; font-size: 12px; opacity: .85; }
.close {
  width: 30px; height: 30px;
  display: grid; place-items: center;
  border-radius: 8px;
  color: var(--bc-accent-ink);
  opacity: .85;
}
.close:hover { opacity: 1; background: rgba(255,255,255,.15); }

.body { padding: 16px; }

.hint { margin: 0; font-size: 13px; color: var(--bc-muted); }

.primary {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--bc-accent);
  color: var(--bc-accent-ink);
  font-size: 15px;
  font-weight: 500;
}
.primary[disabled] { opacity: .6; cursor: not-allowed; }

.danger {
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--bc-danger);
  color: #fff;
  font-size: 15px; font-weight: 500;
}

.ghost {
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 11px 16px;
  border-radius: 12px;
  border: 1px solid var(--bc-border);
  color: var(--bc-text);
  font-size: 14px;
}
.ghost:hover { background: #f9fafb; }

.row { display: grid; gap: 8px; margin-top: 12px; }

/* ── Live call state ──────────────────────────────────────────────────────── */
.status {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: var(--bc-muted);
  margin: 0 0 12px;
}
.dot {
  position: relative;
  width: 8px; height: 8px; border-radius: 50%;
  background: #16a34a;
  flex: none;
}
.pulse::after {
  content: "";
  position: absolute; inset: 0;
  border-radius: 50%;
  background: #16a34a;
  animation: bc-ping 1.4s cubic-bezier(0,0,.2,1) infinite;
}
@keyframes bc-ping { 75%, 100% { transform: scale(2.4); opacity: 0; } }

.timer { font-variant-numeric: tabular-nums; color: var(--bc-text); font-weight: 500; }

/* Live captions. Present for accessibility as much as polish — a deaf or hard-of-hearing
   visitor can follow the agent, and everyone benefits in a noisy room. */
.captions {
  margin-top: 12px;
  max-height: 132px;
  overflow-y: auto;
  border-top: 1px solid var(--bc-border);
  padding-top: 10px;
  font-size: 13px;
}
.captions[hidden] { display: none; }
.line { margin: 0 0 6px; }
.who { font-weight: 600; }
.who.agent { color: var(--bc-accent); }
.who.you   { color: var(--bc-text); }
.said { color: #4b5563; }

/* ── Messages ─────────────────────────────────────────────────────────────── */
.note {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  background: #fef2f2;
  color: #991b1b;
}
.note[hidden] { display: none; }

.legal {
  margin: 12px 0 0;
  font-size: 11px;
  color: var(--bc-muted);
}
`;
